import { Component, Output, EventEmitter, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoanApplicationItem, LoanApplicationUpdateDTO } from '../../loan-applications/loan-applications.interface';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { ToastrService } from 'ngx-toastr';
import { ProductDTO } from '../../Interface & Service/product/product.interface';
import { LosStateService } from '../los-state.service';

@Component({
  selector: 'app-application-create',
  templateUrl: './application-create.component.html',
  styleUrls: ['./application-create.component.css']
})
export class ApplicationCreateComponent implements OnInit {
  @Output() next = new EventEmitter<void>();

  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService = inject(UserLoginService);
  private losState = inject(LosStateService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationData: LoanApplicationItem | null = null;
  productData: ProductDTO | null = null;

  instituteName = '';
  instituteType = 'School / College';
  beneficiaryId = '';
  name = '';
  feeAmount: number | string = '';
  loanAmount: number | string = '';
  tenureMonths: number = 0;
  emiPreview: number = 0;
  isSaving = false;

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        if (app) this.populateData(app);
      });

    this.losState.productData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(prod => {
        this.productData = prod;
        this.calculateEmi();
      });
  }

  populateData(app: LoanApplicationItem): void {
    this.name = app.userName || '';
    this.feeAmount = app.requestedAmount || 0;
    this.loanAmount = app.requestedAmount || 0;
    this.beneficiaryId = app.prrn || '';
    this.tenureMonths = app.tenureMonths || 0;
    this.instituteName = app.businessName || '';

    const module = app.otherDetail1 || '';
    if (module === 'hospital') {
      this.instituteType = 'Hospital / Clinic';
    } else if (module === 'coaching') {
      this.instituteType = 'Coaching Institute';
    } else {
      this.instituteType = 'School / College';
    }

    this.calculateEmi();
  }

  calculateEmi(): void {
    const principal = Number(this.loanAmount) || 0;
    const months = Number(this.tenureMonths) || 0;
    const annualRate = Number(this.productData?.interestRate) || 0;

    if (principal <= 0 || months <= 0) {
      this.emiPreview = 0;
      return;
    }

    const interest =
      principal * (annualRate / 100) / 12 * months;

    const totalPayable = principal + interest;

    this.emiPreview = Number((totalPayable / months).toFixed(2));
  }

  onAmountOrTenureChange(): void {
    this.calculateEmi();
  }

  saveAndContinue(): void {
    if (!this.applicationData) return;
    if (this.isSaving) return;

    const amt = Number(this.loanAmount);
    if (isNaN(amt) || amt <= 0) {
      this.toastr.warning('Please enter a valid loan amount');
      return;
    }
    if (this.tenureMonths <= 0) {
      this.toastr.warning('Please enter a valid tenure');
      return;
    }

    this.isSaving = true;
    const payload: LoanApplicationUpdateDTO = {
      applicationId: this.applicationData.applicationId,
      userId: this.applicationData.userId,
      productId: this.applicationData.productId,
      applicationStatus: 'ConsentPending',
      requestedAmount: amt,
      approvedAmount: this.applicationData.approvedAmount,
      tenureMonths: this.tenureMonths,
      applicationDate: this.applicationData.applicationDate,
      approvalDate: this.applicationData.approvalDate,
      approvedBy: this.applicationData.approvedBy || '',
      rejectionReason: this.applicationData.rejectionReason || '',
      rejectedBy: this.applicationData.rejectedBy || '',
      isDisbursed: this.applicationData.isDisbursed,
      disbursedOn: this.applicationData.disbursedOn,
      disbursedAmount: this.applicationData.disbursedAmount,
      subUserId: this.applicationData.subUserId,
      businessId: this.applicationData.businessId,
      productCode: this.applicationData.productCode || '',
      prrn: this.applicationData.prrn,
      lmsApplicationName: this.applicationData.lmsApplicationName,
      otherDetail1: this.applicationData.otherDetail1,
      otherDetail2: this.applicationData.otherDetail2,
      otherDetail3: this.applicationData.otherDetail3,
      otherDetail4: this.applicationData.otherDetail4,
      otherDetail5: this.applicationData.otherDetail5,
      otherDetail6: this.applicationData.otherDetail6,
      actionUser: this.userLoginService.User()?.userId?.toString() || '0',
      isActive: 1
    };

    this.loanApplicationService.LoanApplication_Update(payload).subscribe({
      next: (res) => {
        this.losState.patchApplication(res);
        this.toastr.success('Application updated successfully');
        this.isSaving = false;
        this.next.emit();
      },
      error: (err) => {
        this.toastr.error('Failed to update application');
        console.error(err);
        this.isSaving = false;
      }
    });
  }
}
